import { useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { defaultButtonClasses } from "@/constants/ui";
import { useUpdateBookingRoom } from "@/hooks/useUpdateBookingRoom";
import { AlertCircle, ArrowRight, CheckCircle2, DoorOpen } from "lucide-react";
import RoomSwatch from "../RoomSwatch/RoomSwatch";
import { useChangeBookingFlow } from "./ChangeBookingFlowProvider";
import type { Room } from "@/types/Room";

type RoomConfirmationProps = {
  onClose?: () => void;
  newRoom: Room;
};

const RoomConfirmation = ({ onClose, newRoom }: RoomConfirmationProps) => {
  const { booking, navigateTo, resetFlow } = useChangeBookingFlow();
  const updateBookingMutation = useUpdateBookingRoom();
  const [error, setError] = useState<string | null>(null);

  const formatTime = (time: string) => {
    // Convert HHmm to HH:mm
    return `${time.slice(0, 2)}:${time.slice(2)}`;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-GB", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleConfirm = async () => {
    if (!booking) return;

    // Clear any previous errors
    setError(null);

    try {
      console.log("Updating booking room:", {
        bookingId: booking.id,
        newRoomId: newRoom.id,
      });

      // Call the update booking room API
      await updateBookingMutation.mutateAsync({
        bookingId: booking.id,
        newRoomId: newRoom.id,
      });

      console.log("Booking room updated successfully");

      // Move to final confirmation screen
      navigateTo("confirmation");
    } catch (err) {
      console.error("Failed to update booking room:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to update booking room. Please try again.";
      setError(errorMessage);
    }
  };

  const handleCancel = () => {
    resetFlow();
    onClose?.();
  };

  if (!booking) {
    return (
      <div className="p-6">
        <p className="text-red-600">Error: No booking data available</p>
        <Button onClick={handleCancel} className="mt-4">
          Close
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-[90vh] lg:min-h-[70vh] w-full flex flex-col bg-white rounded-xl my-4">
      {/* Header - Mobile only */}
      <div className="lg:hidden p-4 py-6 flex flex-col circle-pattern-background circle-pattern-indigo-50">
        <h2 className="text-2xl font-semibold mb-2">Confirm Room Change</h2>
        <p className="text-gray-600 text-xs">Review your changes and confirm to update your booking room.</p>
      </div>

      {/* Desktop grid layout */}
      <div className="hidden lg:grid lg:grid-cols-3 grow gap-1">
        {/* Left column - Information */}
        <div className="p-6 lg:rounded-lg flex flex-col circle-pattern-background circle-pattern-indigo-50">
          <h2 className="text-3xl font-semibold mb-4">Confirm Changes</h2>
          <p className="mb-2 text-gray-600 text-sm">
            Please review the changes below. Once confirmed, your booking will be moved to the new room.
          </p>
        </div>

        {/* Right column - Confirmation Details */}
        <div className="bg-gray-50 rounded-lg lg:rounded-r-xl lg:col-span-2 p-6 border-l border-gray-200 shadow-sm">
          {renderContent()}
        </div>
      </div>

      {/* Mobile content area */}
      <div className="lg:hidden flex-1 bg-gray-50 p-6">{renderContent()}</div>
    </div>
  );

  function renderContent() {
    return (
      <div className="flex flex-col gap-4">
        {/* API Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Update Failed</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Success Alert */}
        <Alert className="border-green-200 bg-green-50 text-green-800">
          <CheckCircle2 className="h-6 w-6 mt-1 text-green-800" />
          <AlertTitle className="text-green-800 text-lg">Ready to Update</AlertTitle>
          <AlertDescription className="text-green-800">
            The new room is available for your booking at this time.
          </AlertDescription>
        </Alert>

        {/* Current vs New Room Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Current Room */}
          <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-3 text-gray-700">Current Room</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <DoorOpen className="w-4 h-4 text-gray-700" />
                <span className="text-gray-700">
                  {booking && booking.room?.name && (
                    <RoomSwatch swatch={booking.room.swatch ?? ""} name={booking.room.name} />
                  )}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm mt-2">
                <span className="text-gray-700">
                  {booking &&
                    `${formatDate(booking.date)} at ${formatTime(booking.startTime)} - ${formatTime(booking.endTime)}`}
                </span>
              </div>
            </div>
          </div>

          {/* New Room */}
          <div className="bg-indigo-50 border-2 border-indigo-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-3 text-indigo-800">New Room</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <DoorOpen className="w-4 h-4 text-indigo-800" />
                <span className="text-indigo-900 font-medium">
                  <RoomSwatch swatch={newRoom.swatch ?? ""} name={newRoom.name ?? ""} />
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm mt-2">
                <span className="text-indigo-800">
                  {booking &&
                    `${formatDate(booking.date)} at ${formatTime(booking.startTime)} - ${formatTime(booking.endTime)}`}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Change Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-blue-800 mb-1">What's changing?</h4>
              <div className="flex items-center gap-2 text-sm text-blue-800 flex-wrap">
                <span>Booking room</span>
                <ArrowRight className="w-4 h-4" />
                <span className="font-medium">
                  {booking && booking.room?.name && (
                    <>
                      from {booking.room.name} to {newRoom.name}
                    </>
                  )}
                </span>
              </div>
              <p className="text-sm text-blue-800 mt-2">
                Your booking date and time will remain the same. Only the room is changing.
              </p>
            </div>
          </div>
        </div>

        {/* Recurring Booking Note */}
        {booking && booking.recurrence && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-900">
              <strong>Note:</strong> This is a recurring booking. This change will apply to all future occurrences of
              this booking.
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col gap-2 mt-6">
          <Button
            className={`w-full ${defaultButtonClasses}`}
            onClick={handleConfirm}
            disabled={updateBookingMutation.isPending}
          >
            {updateBookingMutation.isPending ? (
              <>
                <Spinner className="size-4 mr-2" />
                Updating booking...
              </>
            ) : (
              "Confirm Change"
            )}
          </Button>
          <Button variant="ghost" className="w-full text-gray-600" onClick={handleCancel}>
            Close
          </Button>
        </div>
      </div>
    );
  }
};

export default RoomConfirmation;
